import type { ComponentProps } from 'react';
import React from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { FormattedWebItem } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import type { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { ConvertPageToFolderDialog } from '@confluence/folder-conversion/entry-points/ConvertPageToFolderDialog.ts';
import { useCanConvertPageToFolder } from '@confluence/folder-conversion/entry-points/useCanConvertPageToFolder.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';

import { LazyContentToolsItem } from './LazyContentToolsItem.tsx';

export const ConvertToFolderItem = ({
	sectionItem,
	contentId,
	spaceKey,
	dialogs,
	shouldHighlight,
	onClick,
	...props
}: ConvertToFolderItemProps): React.JSX.Element | null => {
	const { canConvertPageToFolder, contentTitle, error } = useCanConvertPageToFolder({
		contentId,
		spaceKey,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const openDialog = (e: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
		onClick?.(e);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'convertPageToFolder',
				source: 'contentToolsItem',
			},
		}).fire();
		dialogs.showDialog(ConvertPageToFolderDialog, {
			contentId,
			spaceKey,
			contentTitle,
		});
	};

	if (!canConvertPageToFolder) {
		return error ? <ErrorDisplay error={error} /> : null;
	}

	return (
		<>
			{error && <ErrorDisplay error={error} />}
			<LazyContentToolsItem
				key={sectionItem.completeKey}
				{...sectionItem}
				url=""
				{...props}
				onClick={openDialog}
				// already highlighted when showing onboarding
				shouldHighlight={shouldHighlight}
			/>
		</>
	);
};

type ConvertToFolderItemProps = {
	sectionItem: FormattedWebItem;
	contentId: string;
	spaceKey: string;
	dialogs: DialogsStateContainer;
	shouldHighlight?: boolean;
} & Partial<ComponentProps<typeof LazyContentToolsItem>>;
